/**
 * travelRatesResolver — pure, fail-closed Auflösung von Reisekosten-Sätzen für ein Datum.
 *
 * Kein Jahr-Fallback, NIE geraten: fehlt ein zum Reisedatum passender Satz-Eintrag in der
 * versionierten `TravelRatesConfig`, liefert der Resolver `null` — der Aufrufer
 * (`calculateTravelAllowances`) muss das als Fehler (`noRatesForDate`) behandeln, statt
 * einen benachbarten Zeitraum zu approximieren (architecture-reisekosten.md §3.1/E6).
 *
 * Datumsvergleich erfolgt als lexikografischer String-Vergleich auf dem Format 'YYYY-MM-DD':
 * für konsistent zero-padded ISO-Datumsstrings ist die lexikografische Ordnung identisch zur
 * chronologischen Ordnung, das macht die Auflösung deterministisch ohne Date-Parsing/Timezone-
 * Fallstricke.
 *
 * @module travelRatesResolver
 */

import type {
  TravelPerDiemRateEntry,
  TravelMealReductionEntry,
  TravelMileageRateEntry,
  TravelRatesConfig,
  TravelForeignRatesConfig,
  TravelForeignRatesVintage,
} from './travelRates';

/** Striktes 'YYYY-MM-DD'-Muster — Grundlage des lexikografischen Datumsvergleichs. */
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Prüft, ob `dateISO` im Gültigkeitsfenster [validFromISO, validToISO] liegt (beide Grenzen
 * inklusiv). `validToISO === null` bedeutet ein offenes, aktuell gültiges Fenster.
 */
function isWithinValidity(dateISO: string, validFromISO: string, validToISO: string | null): boolean {
  if (dateISO < validFromISO) {
    return false;
  }
  if (validToISO !== null && dateISO > validToISO) {
    return false;
  }
  return true;
}

/**
 * Liefert den zum angegebenen Reisedatum + Land passenden Verpflegungspauschale-Eintrag,
 * oder `null` wenn kein Eintrag zutrifft (fail-closed — nie raten).
 *
 * Bei mehreren gleichzeitig zutreffenden Einträgen (sollte bei sauber gepflegter Config
 * nicht vorkommen) gewinnt der mit dem spätesten `validFromISO` (jüngste, spezifischste
 * Fassung) — deterministisch statt Insertion-Order-abhängig.
 */
export function resolvePerDiemRate(
  dateISO: string,
  countryCode: string,
  cfg: TravelRatesConfig
): TravelPerDiemRateEntry | null {
  const matches = cfg.perDiem.filter(
    (entry) => entry.countryCode === countryCode && isWithinValidity(dateISO, entry.validFromISO, entry.validToISO)
  );
  if (matches.length === 0) {
    return null;
  }
  return matches.reduce((latest, entry) => (entry.validFromISO > latest.validFromISO ? entry : latest));
}

/**
 * Liefert den zum angegebenen Reisedatum passenden Mahlzeitenkürzungs-Eintrag (20/40/40 seit
 * 2020), oder `null` wenn kein Eintrag zutrifft (fail-closed).
 *
 * Mahlzeitenkürzung ist im MVP länderunabhängig modelliert (nur Inland relevant).
 */
export function resolveMealReductions(dateISO: string, cfg: TravelRatesConfig): TravelMealReductionEntry | null {
  const matches = cfg.mealReductions.filter((entry) => isWithinValidity(dateISO, entry.validFromISO, entry.validToISO));
  if (matches.length === 0) {
    return null;
  }
  return matches.reduce((latest, entry) => (entry.validFromISO > latest.validFromISO ? entry : latest));
}

/**
 * Liefert den zum angegebenen Reisedatum passenden Kilometergeld-Eintrag (0,30 €/0,20 € seit
 * 2020), oder `null` wenn kein Eintrag zutrifft (fail-closed).
 */
export function resolveMileageRate(dateISO: string, cfg: TravelRatesConfig): TravelMileageRateEntry | null {
  const matches = cfg.mileage.filter((entry) => isWithinValidity(dateISO, entry.validFromISO, entry.validToISO));
  if (matches.length === 0) {
    return null;
  }
  return matches.reduce((latest, entry) => (entry.validFromISO > latest.validFromISO ? entry : latest));
}

// ---------------------------------------------------------------------------------------------
// Auslands-Resolver (NEU RK-11, architecture-reisekosten.md §13.4, Ausbau-Welle A).
// `resolvePerDiemRate` (oben) bleibt SIGNATUR-UNVERÄNDERT — Auslands-Einträge leben
// ausschließlich in der separaten `TravelForeignRatesConfig` und erreichen den Inland-Resolver
// nie. Diese Sektion ist rein additiv; sie ändert kein bestehendes Inland-Verhalten.
// ---------------------------------------------------------------------------------------------

/**
 * Herkunfts-Kennzeichnung eines aufgelösten Auslands-Satzes. Wandert unverändert (Key-Name)
 * in den Tages-Snapshot des Rechenkerns (`TravelDayCalcResult.rateSource`, RK-14/§13.5/§13.9),
 * damit jede abgerechnete Reise nachvollziehbar bleibt, WELCHE Auflösungsstufe gegriffen hat.
 */
export type ForeignRateSource = 'countryRegion' | 'countryGeneral' | 'luxembourgFallback';

/** Ergebnis einer erfolgreichen Auslands-Satz-Auflösung: der Eintrag + seine Herkunftsstufe. */
export interface ForeignRateResolution {
  entry: TravelPerDiemRateEntry;
  rateSource: ForeignRateSource;
}

/**
 * Liefert unter mehreren zutreffenden Einträgen den mit dem spätesten `validFromISO`
 * (jüngste, spezifischste Fassung) — identisches Tie-Break-Verhalten zu den Inland-Resolvern
 * oben (deterministisch statt Insertion-Order-abhängig).
 */
function pickLatestByValidFrom(entries: TravelPerDiemRateEntry[]): TravelPerDiemRateEntry {
  return entries.reduce((latest, entry) => (entry.validFromISO > latest.validFromISO ? entry : latest));
}

// ---------------------------------------------------------------------------------------------
// Zusicherungs-Horizont (A-6, architecture-travel-rates-validity.md §6, D-2 Ebene 1).
// ---------------------------------------------------------------------------------------------

/**
 * Prüft, ob ein Reisedatum innerhalb des internen Zusicherungs-Horizonts der Auslands-Konfiguration
 * liegt (`assuredUntilISO`, INKLUSIV) — das Prädikat der oberen Fail-closed-Grenze (A-6, D-1/D-2).
 *
 * Ohne diesen Guard wäre der Auslands-Datensatz nur nach UNTEN dicht (`validFromISO`): ein Reisetag
 * am 15.03.2027 würde still mit den 2026er Beträgen gerechnet, weil alle Einträge `validToISO: null`
 * (zeitlich offen) tragen — falsche Zahlen in Buchführung und Steuererklärung, ohne dass jemand einen
 * Fehler macht oder ihn bemerkt. Eine fehlende Zahl ist erkennbar, eine falsche nicht.
 *
 * Verglichen wird AUSSCHLIESSLICH das REISEDATUM (Input), nie `Date.now()` — dadurch ist das gesamte
 * Verhalten ohne Clock-Mocks deterministisch testbar (D-3/§11) und ein bereits abgerechneter Vorgang
 * ändert sein Ergebnis nie nachträglich.
 *
 * Fail-closed ohne Ausnahme: ist `assuredUntilISO` leer, fehlend oder nicht im Format 'YYYY-MM-DD',
 * liefert die Funktion für JEDES Datum `false`. Es gibt bewusst KEINEN `?? <default>`-Fallback —
 * ein defekter Horizont darf nie in „alles erlaubt" umschlagen.
 *
 * @param dateISO Reisedatum ('YYYY-MM-DD') des zu prüfenden Tages.
 * @param foreign Auslands-Sätze-Konfiguration (generiertes Asset oder Test-Config).
 * @returns `true`, wenn `dateISO <= assuredUntilISO` (String-Vergleich auf ISO-Datum).
 */
export function isForeignDateAssured(dateISO: string, foreign: TravelForeignRatesConfig): boolean {
  const horizon = foreign?.assuredUntilISO;
  if (typeof horizon !== 'string' || !ISO_DATE_PATTERN.test(horizon)) {
    return false;
  }
  if (!ISO_DATE_PATTERN.test(dateISO)) {
    return false;
  }
  return dateISO <= horizon;
}

/**
 * Liefert den Jahrgang (Vintage), dessen Gültigkeitsfenster das angegebene Reisedatum enthält,
 * oder `null` (fail-closed, nie raten).
 *
 * Dieselbe Fenster-Logik wie die Satz-Auflösung (`isWithinValidity`) — damit können Anzeige und
 * Berechnung nicht auseinanderlaufen (R4-1: die frühere Anzeige-Auswahl „offener Jahrgang, sonst
 * letzter" war datumsunabhängig und hätte ab dem zweiten Jahrgang eine andere Fundstelle
 * ausgewiesen, als tatsächlich gerechnet wurde).
 *
 * BEWUSST OHNE Horizont-Check: die Quellen-Anzeige eines bereits abgerechneten Alt-Trips muss ihre
 * Fundstelle auch nach Ablauf des Horizonts noch finden (GoBD-Nachweis der Bemessungsgrundlage).
 * Die Sperre wirkt in `resolveForeignPerDiemRate` und im Rechenkern, nicht in der Metadaten-Anzeige.
 *
 * @param dateISO Reisedatum ('YYYY-MM-DD').
 * @param foreign Auslands-Sätze-Konfiguration.
 * @returns Der zutreffende Jahrgang (Tie-Break: spätestes `validFromISO`) oder `null`.
 */
export function resolveForeignVintageForDate(
  dateISO: string,
  foreign: TravelForeignRatesConfig
): TravelForeignRatesVintage | null {
  const vintages = foreign?.vintages;
  if (!vintages || vintages.length === 0) {
    return null;
  }
  const matches = vintages.filter((vintage) =>
    isWithinValidity(dateISO, vintage.validFromISO, vintage.validToISO)
  );
  if (matches.length === 0) {
    return null;
  }
  return matches.reduce((latest, vintage) => (vintage.validFromISO > latest.validFromISO ? vintage : latest));
}

/**
 * Liefert alle Jahrgänge, die für eine Menge von Reisetagen tatsächlich zur Anwendung kämen —
 * dedupliziert nach `validFromISO`, chronologisch aufsteigend sortiert.
 *
 * Grundlage der Quellen-Anzeige (§8): eine Reise über den Jahreswechsel weist dadurch ZWEI
 * BMF-Fundstellen aus statt einer pauschal gewählten. Tage ohne passenden Jahrgang werden still
 * verworfen (fail-safe: lieber keine Fundstelle als eine falsche).
 *
 * @param dayDatesISO Reisedaten ('YYYY-MM-DD') der tatsächlich über Auslands-Sätze gerechneten Tage.
 * @param foreign Auslands-Sätze-Konfiguration.
 * @returns Chronologisch sortierte, duplikatfreie Jahrgangsliste (ggf. leer).
 */
export function resolveAppliedForeignVintages(
  dayDatesISO: readonly string[],
  foreign: TravelForeignRatesConfig
): TravelForeignRatesVintage[] {
  const applied: TravelForeignRatesVintage[] = [];
  for (const dateISO of dayDatesISO) {
    const vintage = resolveForeignVintageForDate(dateISO, foreign);
    if (vintage === null) {
      continue;
    }
    if (applied.some((known) => known.validFromISO === vintage.validFromISO)) {
      continue;
    }
    applied.push(vintage);
  }
  return applied.sort((a, b) =>
    a.validFromISO < b.validFromISO ? -1 : a.validFromISO > b.validFromISO ? 1 : 0
  );
}

/**
 * Auflöst einen Verpflegungspauschale-Satz für ein AUSLANDS-Land zu einem Reisedatum, pure und
 * fail-closed (architecture-reisekosten.md §13.4, E10/E11).
 *
 * **3-stufige Auflösungs-Kaskade** (erste zutreffende Stufe gewinnt):
 * 1. **`countryRegion`** — exakter Treffer auf `(countryCode, regionKey)` im Gültigkeitsfenster.
 *    Wird nur versucht, wenn `regionKey !== null` (der Reisetag eine Stadt/Region angibt).
 * 2. **`countryGeneral`** — Treffer auf `(countryCode)` mit einem Land-Ebene-Eintrag
 *    (`regionKey` im Asset `null`/`undefined` — die BMF-„im Übrigen"-Zeile). Diese Stufe greift
 *    ebenfalls, wenn ein angefragter `regionKey` im Ziel-Jahrgang nicht (mehr) existiert
 *    („Stadt entfallen ⇒ Land-Ebene", Soll-Paar §13.6 #34).
 * 3. **`luxembourgFallback`** — kein Eintrag für `countryCode` (weder Region- noch Land-Ebene)
 *    ⇒ BMF-Auffangregel: Satz von `'LU'` (Luxemburg, Land-Ebene). Dies ist die einzig zulässige
 *    Auffangregel (E11) und wird transparent über `rateSource` gekennzeichnet — es wird NIE
 *    unmarkiert ein falscher Satz verwendet. Die Quell-Config MUSS je Jahrgang einen
 *    `'LU'`-Eintrag enthalten (Skript-Plausibilitäts-Pflicht §13.3); fehlt er dennoch
 *    (z. B. defekte/handgebaute Test-Config), läuft der Auffang fail-closed ins Leere → Stufe 4.
 * 4. **`null`** — kein Land-, kein Region-, kein Luxemburg-Treffer für das Datum. Der Aufrufer
 *    (`calculateTravelAllowances`) behandelt das als `noRatesForDate` (kein Jahr-/Land-Raten,
 *    identisch zur Inland-Resolver-Philosophie, E6).
 *
 * **🔴 Rechtsgrundlage (verbindlich, reisekosten-satz-update-recherche.md §2.2):** Diese Funktion
 * darf AUSSCHLIESSLICH gegen eine `TravelForeignRatesConfig` aufgelöst werden, deren Einträge aus
 * dem jährlichen BMF-Schreiben nach § 9 Abs. 4a EStG stammen (bundesfinanzministerium.de) — NIE
 * gegen die ARVVwV-Sätze des TMS-Portals (tms.bund.de, andere Rechtsgrundlage für
 * Bundesbedienstete, abweichende Beträge). Diese Funktion selbst kann die Quelle einer
 * übergebenen Config nicht prüfen; die Herkunftsgarantie liegt bei der Update-Pipeline
 * (RK-12/RK-13, Skript-Plausibilitätschecks + menschliches Freigabe-Gate O-12).
 *
 * Bei mehreren gleichzeitig zutreffenden Einträgen je Stufe gewinnt der mit dem spätesten
 * `validFromISO` (siehe {@link pickLatestByValidFrom}).
 *
 * @param dateISO Reisedatum ('YYYY-MM-DD') — Satz-Auswahl erfolgt strikt nach dem REISEDATUM
 *   je Tag, nie nach dem Abrechnungsdatum (V17).
 * @param countryCode ISO-3166-1 alpha-2 Ländercode des Reisetags (z. B. `'US'`, `'XX'` für ein
 *   nicht gelistetes Land — löst dann Stufe 3 aus).
 * @param regionKey gewählte Stadt/Region des Tages, oder `null` wenn keine gewählt wurde
 *   (Land-Ebene).
 * @param foreign die Auslands-Sätze-Konfiguration (generiertes Asset `TRAVEL_RATES_FOREIGN`,
 *   RK-13, oder eine synthetische Test-Config).
 */
export function resolveForeignPerDiemRate(
  dateISO: string,
  countryCode: string,
  regionKey: string | null,
  foreign: TravelForeignRatesConfig
): ForeignRateResolution | null {
  // 🔴 A-6 / D-2 Ebene 1: obere Fail-closed-Grenze VOR jeder Kaskadenstufe. Deckt damit auch
  // alle Nicht-Rechenkern-Konsumenten ab (z. B. die Mahlzeiten-Tooltips über
  // `getMealReductionPreviewCents`), ohne dass diese den Horizont selbst kennen müssen.
  if (!isForeignDateAssured(dateISO, foreign)) {
    return null;
  }

  if (regionKey !== null) {
    const regionMatches = foreign.entries.filter(
      (entry) =>
        entry.countryCode === countryCode &&
        entry.regionKey === regionKey &&
        isWithinValidity(dateISO, entry.validFromISO, entry.validToISO)
    );
    if (regionMatches.length > 0) {
      return { entry: pickLatestByValidFrom(regionMatches), rateSource: 'countryRegion' };
    }
  }

  const generalMatches = foreign.entries.filter(
    (entry) =>
      entry.countryCode === countryCode &&
      (entry.regionKey === null || entry.regionKey === undefined) &&
      isWithinValidity(dateISO, entry.validFromISO, entry.validToISO)
  );
  if (generalMatches.length > 0) {
    return { entry: pickLatestByValidFrom(generalMatches), rateSource: 'countryGeneral' };
  }

  const luxembourgMatches = foreign.entries.filter(
    (entry) =>
      entry.countryCode === 'LU' &&
      (entry.regionKey === null || entry.regionKey === undefined) &&
      isWithinValidity(dateISO, entry.validFromISO, entry.validToISO)
  );
  if (luxembourgMatches.length > 0) {
    return { entry: pickLatestByValidFrom(luxembourgMatches), rateSource: 'luxembourgFallback' };
  }

  return null;
}
